// frontend/src/pages/ContactPage.tsx
import { useState, type FormEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { getPublicContactDetails } from "@/api/settingService";
import { submitContactForm } from "@/api/contactService";
import {
  type OfficeAddressSettingValue,
  type ContactFormPayload,
  type PublicContactInfo,
} from "@/types";
import { useSiteSettingsStore } from '@/store/settingsStore'; // Import settings store

const defaultOfficeAddress: OfficeAddressSettingValue = {
  line1: "Vedas Trading Headquarters",
  line2: "100 Financial Plaza, Suite 500",
  city: "New York",
  state: "NY",
  zip: "10001",
  country: "USA",
  maps_url: "https://maps.google.com/?q=100+Financial+Plaza,New+York,NY", 
};

const defaultContactInfo: PublicContactInfo = {
  contact_office_address_json: defaultOfficeAddress,
  contact_phone_general: "+1 (800) 555-0100",
  contact_email_support: "support@vedastrading.com",
  contact_email_sales: "sales@vedastrading.com",
};

const ContactPage = () => {
  const [formData, setFormData] = useState<ContactFormPayload>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [contactInfo, setContactInfo] = useState<PublicContactInfo>(defaultContactInfo);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true); // For contact details loading
  
  // Get settings for dynamic header
  const publicSiteSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSiteSettings = useSiteSettingsStore(state => state.isLoading); // For general site settings like header text

  const pageTitle = isLoadingSiteSettings
    ? "Loading..."
    : publicSiteSettings?.contact_page_header_title || "Contact Us";
  const pageSubtitle = isLoadingSiteSettings
    ? "Loading description..."
    : publicSiteSettings?.contact_page_header_subtitle || "We're here to help. Reach out to us with any questions, inquiries, or feedback.";


  useEffect(() => {
    const fetchContactDetails = async () => {
      setIsLoadingSettings(true); // For contact details specifically
      try {
        const fetchedData = await getPublicContactDetails();
        console.log(fetchedData)
        const mergedAddress: OfficeAddressSettingValue = {
          line1: fetchedData.contact_office_address_json?.line1 ?? defaultOfficeAddress.line1,
          line2: fetchedData.contact_office_address_json?.line2 ?? defaultOfficeAddress.line2,
          city: fetchedData.contact_office_address_json?.city ?? defaultOfficeAddress.city,
          state: fetchedData.contact_office_address_json?.state ?? defaultOfficeAddress.state,
          zip: fetchedData.contact_office_address_json?.zip ?? defaultOfficeAddress.zip,
          country: fetchedData.contact_office_address_json?.country ?? defaultOfficeAddress.country,
          maps_url: fetchedData.contact_office_address_json?.maps_url ?? defaultOfficeAddress.maps_url,
        };
        
        const mergedContactInfo: PublicContactInfo = {
          contact_office_address_json: mergedAddress,
          contact_phone_general: fetchedData.contact_phone_general ?? defaultContactInfo.contact_phone_general,
          contact_email_support: fetchedData.contact_email_support ?? defaultContactInfo.contact_email_support,
          contact_email_sales: fetchedData.contact_email_sales ?? defaultContactInfo.contact_email_sales,
        };
        setContactInfo(mergedContactInfo);

      } catch (error) {
        console.error("Failed to fetch public contact details:", error);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchContactDetails();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitStatus(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({
        type: "error",
        message: "Name, Email, and Message are required.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await submitContactForm(formData);
      setSubmitStatus({
        type: "success",
        message:
          "Your message has been sent successfully! We will get back to you soon.",
      });
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error: any) {
      console.error("Contact form submission error:", error);
      const message =
        error.response?.data?.detail ||
        error.message ||
        "An unexpected error occurred. Please try again.";
      setSubmitStatus({ type: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const currentOfficeAddress = contactInfo.contact_office_address_json;
  const currentGeneralPhone = contactInfo.contact_phone_general;
  const currentSupportEmail = contactInfo.contact_email_support;
  const currentSalesEmail = contactInfo.contact_email_sales;

  const contactInfoDetails = [
    {
      icon: MapPin,
      title: "Our Office",
      id: "office",
      content: isLoadingSettings ? ( // Use isLoadingSettings for the contact details part
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : currentOfficeAddress ? (
        <>
          <p>{currentOfficeAddress.line1}</p>
          {currentOfficeAddress.line2 && <p>{currentOfficeAddress.line2}</p>}
          <p>
            {currentOfficeAddress.city}, {currentOfficeAddress.state} {currentOfficeAddress.zip}
            {currentOfficeAddress.country && `, ${currentOfficeAddress.country}`}
          </p>
          {currentOfficeAddress.maps_url && currentOfficeAddress.maps_url !== "#" && ( 
            <Button
              variant="outline"
              className="mt-3 border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors"
              asChild
            >
              <a
                href={currentOfficeAddress.maps_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Directions
              </a>
            </Button>
          )}
        </>
      ) : (
        <p>Address information not available.</p> 
      ),
    },
    {
      icon: Phone,
      title: "Phone & Email",
      id: "phoneEmail",
      content: isLoadingSettings ? ( // Use isLoadingSettings for the contact details part
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <>
          {currentGeneralPhone && (
            <p>
              <strong>General Inquiries:</strong>{" "}
              <a
                href={`tel:${currentGeneralPhone}`}
                className="text-primary hover:text-accent hover:underline transition-colors"
              >
                {currentGeneralPhone}
              </a>
            </p>
          )}
          {currentSupportEmail && (
            <p>
              <strong>Support:</strong>{" "}
              <a
                href={`mailto:${currentSupportEmail}`}
                className="text-primary hover:text-accent hover:underline transition-colors"
              >
                {currentSupportEmail}
              </a>
            </p>
          )}
          {currentSalesEmail && (
            <p>
              <strong>Sales:</strong>{" "}
              <a
                href={`mailto:${currentSalesEmail}`}
                className="text-primary hover:text-accent hover:underline transition-colors"
              >
                {currentSalesEmail}
              </a>
            </p>
          )}
          {!isLoadingSettings && !currentGeneralPhone && !currentSupportEmail && !currentSalesEmail && (
            <p>Contact details not available.</p>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 text-foreground bg-background">
      <header className="mb-10 md:mb-16 text-center border-b border-border/60 pb-10 relative">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 md:w-1/3 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"></div>
        <Mail className="h-16 w-16 text-primary mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          {/* Dynamic Page Title */}
          <span dangerouslySetInnerHTML={{ __html: pageTitle.replace(/Contact|Us/g, '<span class="text-gradient-maroon-to-gold">$&</span>') || "Contact <span class=\"text-gradient-maroon-to-gold\">Us</span>" }} />
        </h1>
        <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          {/* Dynamic Page Subtitle */}
          {pageSubtitle}
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <Card className="bg-card text-card-foreground shadow-xl border border-border/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl flex items-center text-foreground">
              <Send className="mr-3 h-6 w-6 text-primary" /> Send Us a Message
            </CardTitle>
            <CardDescription className="text-muted-foreground pt-1">
              Fill out the form below and we'll get back to you as soon as
              possible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Full Name
                </label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  disabled={isSubmitting}
                  className="border-border focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  className="border-border focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Phone Number (Optional)
                </label>
                <Input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone || ""}
                  onChange={handleChange}
                  placeholder="Your phone number"
                  disabled={isSubmitting}
                  className="border-border focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Subject
                </label>
                <Input
                  type="text"
                  name="subject"
                  id="subject"
                  value={formData.subject || ""}
                  onChange={handleChange}
                  placeholder="Regarding your products..."
                  disabled={isSubmitting}
                  className="border-border focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Message
                </label>
                <Textarea
                  name="message"
                  id="message"
                  rows={5}
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your detailed message here..."
                  disabled={isSubmitting}
                  className="border-border focus:border-primary focus:ring-primary min-h-[120px]"
                />
              </div>
              <div>
                <Button
                  type="submit"
                  className="w-full !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
              {submitStatus && (
                <>
                  {submitStatus.type === "error" && (
                    <div className="p-3 !bg-destructive/10 !text-destructive border border-destructive/30 rounded-md flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      {submitStatus.message}
                    </div>
                  )}
                  {submitStatus.type === "success" && (
                    <div className="p-3 bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 rounded-md flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      {submitStatus.message}
                    </div>
                  )}
                </>
              )}
            </form>
          </CardContent>
        </Card>

        <div className="space-y-8">
          {contactInfoDetails.map((info) => {
            const InfoIcon = info.icon;
            return (
              <Card
                key={info.id}
                className="bg-card text-card-foreground shadow-lg border border-border/30 group hover:border-primary/30 transition-colors"
              >
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/50 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-center"></div>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl flex items-center text-foreground group-hover:text-primary transition-colors">
                    <InfoIcon className="mr-3 h-6 w-6 text-primary group-hover:text-accent transition-colors" />
                    {info.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground space-y-2 pt-0">
                  {info.content}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
